import { Config } from "@/config/Config";

let accessTokenMemory: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function getAccessToken(): string | null {
  return accessTokenMemory;
}

export function setAccessToken(token: string | null): void {
  accessTokenMemory = token;
}

export function clearAccessToken(): void {
  accessTokenMemory = null;
  refreshPromise = null;
}

/**
 * Renovação de token de acesso com Single-Flight Lock.
 * Previne requisições concorrentes de refresh token que poderiam
 * disparar a detecção de reuso de token (SECURITY_REUSE_DETECTION) na Auth API.
 */
export async function refreshAccessTokenSingleFlight(): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${Config.authApiUrl}/auth/token`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Id": Config.defaultClientId,
        },
        body: JSON.stringify({
          grant_type: "refresh_token",
          client_id: Config.defaultClientId,
        }),
      });

      if (!response.ok) {
        clearAccessToken();
        return null;
      }

      const data = await response.json();
      accessTokenMemory = data.access_token;
      return accessTokenMemory;
    } catch {
      clearAccessToken();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Cliente HTTP autenticado com injeção automática de Bearer token,
 * envio de cookies (credentials: include) e tratamento transparente de 401
 * utilizando o Single-Flight Lock.
 */
export async function authenticatedFetch(
  endpointOrUrl: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = endpointOrUrl.startsWith("http")
    ? endpointOrUrl
    : `${Config.authApiUrl}${endpointOrUrl.startsWith("/") ? "" : "/"}${endpointOrUrl}`;

  const makeRequest = (token: string | null) => {
    const headers = new Headers(init.headers || {});
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    if (!headers.has("X-Client-Id")) {
      headers.set("X-Client-Id", Config.defaultClientId);
    }
    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }

    return fetch(url, {
      ...init,
      headers,
      credentials: "include",
    });
  };

  let response = await makeRequest(accessTokenMemory);

  // Se o servidor retornar 401 Unauthorized, tenta auto-refresh com Single-Flight Lock
  if (response.status === 401) {
    const newToken = await refreshAccessTokenSingleFlight();
    if (!newToken) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
      }
      return response;
    }
    response = await makeRequest(newToken);
  }

  return response;
}

/**
 * Helper para chamadas com deserialização JSON e tratamento tipado de erros.
 */
export async function apiRequest<T>(endpoint: string, init: RequestInit = {}): Promise<T> {
  const response = await authenticatedFetch(endpoint, init);

  if (!response.ok) {
    let errorMessage = `Erro HTTP ${response.status}`;
    try {
      const errorBody = await response.json();
      if (errorBody && errorBody.message) {
        errorMessage = errorBody.message;
      }
    } catch {
      // Ignora falha de parse JSON no corpo do erro
    }
    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return (await response.json()) as T;
}
